import { makeAutoObservable } from "mobx";
import { v4 as uuidv4 } from "uuid";

import { MealPatternNameKind } from "@/gql/graphql";

import { CellRecipe } from "./CellRecipe";
import { Column } from "./Column";
import MealPatternItem from "./MealPatternItem";
import MealPlanFormStore from "./MealPlanFormStore";
import { PlannedMeal } from "./PlannedMeal";
import { PlannedMealItem } from "./PlannedMealItem";

export class Cell {
  column: Column;
  plannedMeal: PlannedMeal;
  plannedMealItems: PlannedMealItem[] = [];
  mealPatternItems: MealPatternItem[] = [];
  /**
   * Named groups of foods in this cell. A group may be linked to a library
   * Recipe (added from "My Recipes" or promoted via "Save to My Recipes") or
   * be a purely local label the coach typed over some foods.
   */
  cellRecipes: CellRecipe[] = [];

  isDeleting = false;

  private store: MealPlanFormStore;

  get mode(): "meal_pattern_item" | "planned_meal_item" {
    return this.column.mode === "meal_pattern"
      ? "meal_pattern_item"
      : "planned_meal_item";
  }

  get items(): PlannedMealItem[] | MealPatternItem[] {
    return this.mode === "meal_pattern_item"
      ? this.mealPatternItems
      : this.plannedMealItems;
  }

  get key(): string {
    return [this.column.index, this.plannedMeal.uuid].join("_");
  }

  get label(): string {
    return [this.plannedMeal.label, this.column.label].join(" ");
  }

  get totalEnergyKcal(): number {
    return (this.items as Array<PlannedMealItem | MealPatternItem>).reduce(
      (total, item) => total + item.energyKcal,
      0
    );
  }

  get isActive(): boolean {
    return this.store.cells.active === this;
  }

  get isEmpty(): boolean {
    return this.items.length === 0;
  }

  constructor(
    store: MealPlanFormStore,
    plannedMealRow: PlannedMeal,
    dayColumn: Column
  ) {
    makeAutoObservable(this);
    this.store = store;
    this.plannedMeal = plannedMealRow;
    this.column = dayColumn;
  }

  /** Items that are not part of any recipe group. */
  get looseItems(): PlannedMealItem[] {
    return this.plannedMealItems.filter((item) => !item.cellRecipeUuid);
  }

  /** Recreate a recipe group from a persisted CellRecipe record when hydrating. */
  hydrateCellRecipe(
    uuid: string,
    name: string,
    recipeId?: string | null
  ): void {
    this.cellRecipes.push(
      new CellRecipe(this, name, recipeId ?? undefined, uuid)
    );
  }

  /**
   * Add a recipe to the cell: creates a group and copies the recipe's
   * ingredients in as this cell's own items, so the coach can tweak each
   * ingredient's quantity/unit here without touching the library recipe.
   */
  addCellRecipe(
    recipeId: string | undefined,
    name: string,
    ingredients: Array<{
      food: PlannedMealItem["food"];
      foodPortion: PlannedMealItem["foodPortion"];
      amount: number;
    }>
  ): CellRecipe {
    const cellRecipe = new CellRecipe(this, name, recipeId);
    this.cellRecipes.push(cellRecipe);
    ingredients.forEach(({ food, foodPortion, amount }) => {
      this.plannedMealItems.push(
        new PlannedMealItem(
          this.store,
          this,
          food,
          foodPortion,
          amount,
          uuidv4(),
          cellRecipe.uuid
        )
      );
    });
    return cellRecipe;
  }

  /**
   * Turn the cell's loose items into a named group. Without a recipeId this
   * is a purely local label ("name these foods"); the group can later be
   * promoted to a library recipe via CellRecipe#linkRecipe.
   */
  groupLooseItemsIntoCellRecipe(name: string, recipeId?: string): CellRecipe {
    const cellRecipe = new CellRecipe(this, name, recipeId);
    this.cellRecipes.push(cellRecipe);
    this.looseItems.forEach((item) => {
      item.cellRecipeUuid = cellRecipe.uuid;
    });
    return cellRecipe;
  }

  /** Remove a recipe group along with its ingredient items. */
  removeCellRecipe(cellRecipe: CellRecipe): void {
    const removedItems = cellRecipe.items;
    this.store.handleRemovedPlannedMealItems(removedItems);
    this.plannedMealItems = this.plannedMealItems.filter(
      (item) => item.cellRecipeUuid !== cellRecipe.uuid
    );
    const index = this.cellRecipes.indexOf(cellRecipe);
    if (index !== -1) this.cellRecipes.splice(index, 1);
    if (this.isEmpty) this.isDeleting = false;
  }

  addPlannedMealItem(
    food: PlannedMealItem["food"],
    foodPortion: PlannedMealItem["foodPortion"],
    amount: number,
    uuid = uuidv4(),
    cellRecipeUuid?: string
  ): void {
    // Only dedupe against loose items — the same food may legitimately appear
    // again as an ingredient of a recipe group.
    const existing = cellRecipeUuid
      ? undefined
      : this.looseItems.find((i) => i.food.id === food.id);
    // If food item is already added, just update the selected food portion and
    // amount.
    if (existing) {
      existing.foodPortion = foodPortion;
      existing.amount = amount;
    } else {
      this.plannedMealItems.push(
        new PlannedMealItem(
          this.store,
          this,
          food,
          foodPortion,
          amount,
          uuid,
          cellRecipeUuid
        )
      );
    }
  }

  addMealPatternItem(
    name: MealPatternNameKind,
    amountServings: number,
    food: MealPatternItem["food"] | undefined | null,
    foodPortion: MealPatternItem["foodPortion"],
    uuid = uuidv4()
  ): void {
    const existing = this.mealPatternItems.find((i) => i.name === name);
    if (existing) {
      existing.amountServings = amountServings;
    } else {
      this.mealPatternItems.push(
        new MealPatternItem(this, name, amountServings, food, foodPortion, uuid)
      );
    }
  }

  removePlannedMealItem(item: PlannedMealItem): void {
    const index = this.plannedMealItems.indexOf(item);
    if (index !== -1) {
      this.store.handleRemovedPlannedMealItems([item]);
      this.plannedMealItems.splice(index, 1);
    }
    // Drop a recipe group once its last ingredient is removed.
    if (item.cellRecipeUuid) {
      const cellRecipe = this.cellRecipes.find(
        (cr) => cr.uuid === item.cellRecipeUuid
      );
      if (cellRecipe && cellRecipe.items.length === 0) {
        this.cellRecipes.splice(this.cellRecipes.indexOf(cellRecipe), 1);
      }
    }
    if (this.isEmpty) this.isDeleting = false;
  }

  removeMealPatternItem(item: MealPatternItem): void {
    const index = this.mealPatternItems.indexOf(item);
    if (index !== -1) this.mealPatternItems.splice(index, 1);
    if (this.isEmpty) this.isDeleting = false;
  }

  copyFromCell(cell: Cell): void {
    // Do nothing if they're not in the same mode.
    if (cell.mode !== this.mode) return;

    if (cell.mode === "planned_meal_item") {
      this.store.handleRemovedPlannedMealItems([...this.plannedMealItems]);
      // Copy recipe groups under fresh uuids (each CellRecipe record is unique
      // per cell) and remap the copied items onto the new group uuids.
      const copiedGroupUuids = new Map<string, string>();
      this.cellRecipes = cell.cellRecipes.map((cr) => {
        const copy = new CellRecipe(this, cr.name, cr.recipeId);
        copiedGroupUuids.set(cr.uuid, copy.uuid);
        return copy;
      });
      this.plannedMealItems = cell.plannedMealItems.map(
        (i) =>
          new PlannedMealItem(
            this.store,
            this,
            i.food,
            i.foodPortion,
            i.amount,
            uuidv4(),
            i.cellRecipeUuid
              ? copiedGroupUuids.get(i.cellRecipeUuid)
              : undefined
          )
      );
    } else {
      this.mealPatternItems = cell.mealPatternItems.map(
        (i) =>
          new MealPatternItem(
            this,
            i.name,
            i.amountServings,
            i.food,
            i.foodPortion
          )
      );
    }
  }

  replacePlannedMealItems(newItems: PlannedMealItem[]): void {
    const removedItems = this.plannedMealItems.filter(
      (existing) => !newItems.some((newItem) => newItem.uuid === existing.uuid)
    );
    this.store.handleRemovedPlannedMealItems(removedItems);
    this.plannedMealItems = newItems.map(
      (i) =>
        new PlannedMealItem(
          this.store,
          this,
          i.food,
          i.foodPortion,
          i.amount,
          uuidv4(),
          i.cellRecipeUuid
        )
    );
  }

  clearItems(): void {
    this.store.handleRemovedPlannedMealItems([...this.plannedMealItems]);
    this.plannedMealItems = [];
    this.mealPatternItems = [];
    this.cellRecipes = [];
  }

  toggleDeleting(): void {
    this.isDeleting = !this.isDeleting;
  }
}
